import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { VectorFile } from "@/api";
import { getUserVectorFiles } from "@/api/api";
import type { RootState } from "../store";
import { stat } from "fs";

interface VectorFileState {
    vector_file: VectorFile[];
    vector_file_status: "idle" | "loading" | "success" | "failed";
    vector_file_error: string | null;
    deletingFileId: number | null; // Track file being deleted to filter from fetched results
}

const initialState: VectorFileState = {
    vector_file: [],
    vector_file_status: "idle",
    vector_file_error: null,
    deletingFileId: null
};

export const fetchUserVectorFiles = createAsyncThunk<
    VectorFile[], // Return type should be an array
    void,
    { rejectValue: string }
>("vectorFiles/fetchUserVectorFiles", async (_, { getState, rejectWithValue }) => {
    try {
        const state = getState() as RootState;
        const token = state.clientToken.token ?? "";
        const userid = state.userProfiles.userProfile?.userid ?? "";

        if (!token) {
            return rejectWithValue("No token available");
        }

        if (!userid) {
            return rejectWithValue("No user ID available");
        }

        try {
            const response = await getUserVectorFiles(token, userid);
            // If response is an object, extract the array property (e.g., response.vectorFiles)
            // Adjust the property name as per your API response
            console.log("Response from API:", response[0]); // Debugging line
            return response;
        } catch (error: any) {
            console.log("Error in fetchUserVectorFiles:", error); // Debugging line
            return rejectWithValue(error.message || "Failed to fetch vector files");
        }
    } catch (error: any) {
        console.error("Error fetching user vector files:", error);
        return rejectWithValue(error.message || "Failed to fetch user vector files");
    }
});

const vectorFilesSlice = createSlice({
    name: "vectorFiles",
    initialState,
    reducers: {
        removeVectorFile: (state, action: PayloadAction<number>) => {
            state.vector_file = state.vector_file.filter(f => f.fileid !== action.payload);
        },
        setDeletingFileId: (state, action: PayloadAction<number>) => {
            state.deletingFileId = action.payload;
        },
        clearDeletingFileId: (state) => {
            state.deletingFileId = null;
        }
    },
    extraReducers: builder => {
        builder
            .addCase(fetchUserVectorFiles.pending, state => {
                state.vector_file_status = "loading";
                state.vector_file_error = null;
            })
            .addCase(fetchUserVectorFiles.fulfilled, (state, action: PayloadAction<VectorFile[]>) => {
                state.vector_file_status = "success";
                state.vector_file_error = null;
                // Filter out the file being deleted to prevent race condition
                // where backend hasn't fully processed the deletion yet
                state.vector_file = state.deletingFileId
                    ? action.payload.filter(f => f.fileid !== state.deletingFileId)
                    : action.payload;
            })
            .addCase(fetchUserVectorFiles.rejected, (state, action: PayloadAction<string | undefined>) => {
                state.vector_file_status = "failed";
                state.vector_file_error = action.payload ?? "Failed to fetch vector files";
            });
    }
});

export const { removeVectorFile, setDeletingFileId, clearDeletingFileId } = vectorFilesSlice.actions;
export default vectorFilesSlice.reducer;
